import {
  Box,
  CircularProgress,
  Icon,
  Typography,
} from '@/components/muiComponents';
import { LOG_MESSAGES } from '@/constants/logMessages';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { shouldUseDemoAuth } from '@/util/demoUtils';
import { logEvent, logger } from '@/util/logger';
import { fullName } from '@/util/util';
import { datadogLogs } from '@datadog/browser-logs';
import { AccountCircleOutlined } from '@mui/icons-material';
import {
  Avatar,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Popover,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AuthState, UserClaims } from '@okta/okta-auth-js';
import { useOktaAuth } from '@okta/okta-react';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

type oktaLoadingStateType = 'IDLE' | 'LOADING';
export interface AuthContextType {
  oktaLoadingState: oktaLoadingStateType;
  login: () => void;
  logout: () => void;
  authState: AuthState | null;
  userInfo?: UserClaims;
  isAccessDenied?: boolean;
  setIsAccessDenied: (isAccessDenied: boolean) => void;
}

export const AuthButton = ({ closed }: { closed: boolean }) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );
  const [openedMenu, setOpenedMenu] = useState<boolean>(false);
  const theme = useTheme();

  const { authState, login, logout } = useAuth();

  const { profile, profileImage } = useProfile();

  const { t } = useTranslation(['auth']);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    logEvent(LOG_MESSAGES.USER_CLICKED_USER_MENU);
    setAnchorEl(event.currentTarget as HTMLButtonElement);
    setOpenedMenu(false);
  };

  const handleClose = () => {
    logEvent(LOG_MESSAGES.USER_CLOSED_USER_MENU);
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const menuPopout = () => (
    <Box
      id="auth-menu"
      data-testid="auth-menu"
      role="menu"
      aria-orientation="vertical"
      aria-label={
        authState?.isAuthenticated ? 'User menu' : 'Authentication menu'
      }
      sx={{
        position: 'absolute',
        top: '0px',
        left: '100%',
        display: {
          xs: 'none',
          lg: openedMenu && closed ? 'block' : 'none',
        },
        p: '4px',
        borderRadius: '8px',
      }}
    >
      <ListItem disablePadding role="menuitem">
        <ListItemButton
          data-testid="auth-menu-button"
          sx={{
            padding: '12px',
            borderRadius: 3,
            backgroundColor: theme.palette.tooltips.main,
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: theme.palette.tooltips.main,
              opacity: 0.95,
            },
          }}
          onClick={authState?.isAuthenticated ? logout : login}
          aria-label={
            authState?.isAuthenticated
              ? `Sign out as ${fullName(profile)}`
              : 'Sign in'
          }
        >
          <Typography
            variant="bodyMedium"
            sx={{
              color: theme.palette.background.default,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {authState?.isAuthenticated ? `${fullName(profile)}` : 'Sign In'}
          </Typography>
        </ListItemButton>
      </ListItem>
    </Box>
  );

  if (!authState?.isAuthenticated) {
    return (
      <Box
        data-testid="auth-signin-container"
        width="100%"
        display="flex"
        justifyContent="start"
        position="relative"
        alignItems="center"
        mt={2}
        mb={3}
        onMouseEnter={() => setOpenedMenu(true)}
        onMouseLeave={() => setOpenedMenu(false)}
      >
        <ListItemButton
          data-testid="auth-signin-button"
          sx={{
            padding: '12px',
            borderRadius: 3,
          }}
          onClick={login}
          aria-haspopup="menu"
          aria-expanded={openedMenu && closed}
          aria-controls="auth-menu"
        >
          <Box
            display="flex"
            justifyContent="start"
            alignItems="center"
            sx={{ minWidth: 28 }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Icon
                icon={AccountCircleOutlined}
                sx={{ p: '2px' }}
                size="28px"
              />
            </ListItemIcon>
          </Box>
          {!closed && (
            <Typography variant="bodyMedium">{t('signIn')}</Typography>
          )}
        </ListItemButton>
        {menuPopout()}
      </Box>
    );
  }

  return (
    <Box
      data-testid="auth-user-container"
      width="100%"
      display="flex"
      justifyContent="start"
      alignItems="center"
      position="relative"
      mt={2}
      mb={3}
      onMouseEnter={() => setOpenedMenu(true)}
      onMouseLeave={() => setOpenedMenu(false)}
    >
      <ListItemButton
        data-testid="auth-user-button"
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          padding: '12px',
          borderRadius: 3,
        }}
        onClick={handleClick}
        aria-haspopup="menu"
        aria-expanded={openedMenu && closed}
        aria-controls="auth-menu"
      >
        <Avatar
          sx={{
            backgroundColor: theme.palette.secondary.main,
            color: 'white',
            height: '32px',
            width: '32px',
          }}
          src={profileImage}
        >
          {fullName(profile)?.charAt(0) || ''}
        </Avatar>
        {!closed && (
          <Typography
            variant="bodyMedium"
            sx={{ fontWeight: 'bold', marginLeft: '10px' }}
          >
            {fullName(profile)}
          </Typography>
        )}
      </ListItemButton>
      <Popover
        id={id}
        open={open}
        sx={{
          top: '-40px',
          boxShadow: 'none',
          //   backgroundColor: 'white', // Ensure the background is explicitly white
          //   '& .MuiPaper-root': {
          //     // Ensures the popover container is also white
          //     backgroundColor: 'white',
          //   },
        }}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <ListItemButton
          data-testid="auth-logout-button"
          onClick={logout}
          sx={{
            padding: '12px',
            borderRadius: 3,
            boxShadow: 'none',
            backgroundColor: theme.palette.tooltips.background,
            //theme.palette.tooltips.main,
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: theme.palette.tooltips.background,
              // theme.palette.tooltips.main,
              opacity: 0.5,
            },
            color: theme.palette.tooltips.text,
          }}
        >
          {t('logOut') as string}
        </ListItemButton>
      </Popover>
      {menuPopout()}
    </Box>
  );
};

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [loading] = useState(false);
  const [oktaLoadingState, setOktaLoadingState] =
    useState<oktaLoadingStateType>('IDLE'); // we do not need the success or error state.
  const { oktaAuth, authState } = useOktaAuth();
  const [userInfo, setUserInfo] = useState<UserClaims>();
  const [isAccessDenied, setIsAccessDenied] = useState(false);
  useEffect(() => {
    if (authState?.isAuthenticated) {
      oktaAuth?.token?.getUserInfo().then((userInfo) => {
        setUserInfo(userInfo);
      });
    }
  }, [authState?.isAuthenticated, oktaAuth]);

  const login = useCallback(async () => {
    try {
      logEvent(LOG_MESSAGES.USER_ATTEMPTED_LOGIN);

      // Check if this is demo environment
      if (shouldUseDemoAuth()) {
        // Demo login is handled in App.tsx, don't redirect here
        return;
      }

      setOktaLoadingState('LOADING');
      await oktaAuth.signInWithRedirect();
    } catch (error) {
      console.error('Login failed:', error);
      logger.error({
        message: LOG_MESSAGES.LOGIN_FAILED,
        error,
      });
    } finally {
      setOktaLoadingState('IDLE');
    }
  }, [oktaAuth]);

  // useEffect(() => {
  //   console.log('authState?.isAuthenticated', authState?.isAuthenticated);
  //   if (!authState?.isAuthenticated && oktaLoadingState === 'IDLE') {
  //     login();
  //   }
  // }, [authState?.isAuthenticated, oktaAuth, login, oktaLoadingState]);

  const logout = async () => {
    try {
      setOktaLoadingState('LOADING');
      localStorage.removeItem('dismissedAnnouncementIds');

      // In demo environment, just clear local state instead of redirecting to Okta
      if (shouldUseDemoAuth()) {
        // Set flag to indicate manual logout
        localStorage.setItem('demoManualLogout', 'true');
        // Clear tokens and session without Okta redirect
        await oktaAuth.tokenManager.clear();
        datadogLogs.clearUser();
        // Redirect to demo login page instead of reloading
        window.location.href = '/demo/login';
      } else {
        // Normal Okta sign out for non-demo environments
        await oktaAuth.signOut();
      }

      logEvent(LOG_MESSAGES.USER_LOGGED_OUT);
    } catch (error) {
      console.error('Logout failed:', error);
      logger.error({
        message: LOG_MESSAGES.LOGOUT_FAILED,
        error,
      });
    } finally {
      setOktaLoadingState('IDLE');
      if (!shouldUseDemoAuth()) {
        datadogLogs.clearUser();
      }
    }
  };

  return (
    <>
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            margin: 'auto',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <AuthContext.Provider
          value={{
            login,
            logout,
            oktaLoadingState,
            authState,
            userInfo,
            isAccessDenied,
            setIsAccessDenied,
          }}
        >
          {children}
        </AuthContext.Provider>
      )}
    </>
  );
};
export default AuthProvider;
